import React from 'react';
import { Card } from './ui/card';
import { 
  LogOut, 
  DollarSign, 
  Home,
  BarChart3
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';





import { useAuth } from './SupabaseAuth';

// Use favicon as logo
const logoImage = '/images/logos/cf-logo-spaceman.png';

interface InvestorDashboardProps {
  onHome?: () => void;
  onLogout?: () => void;
}

export function InvestorDashboard({ onHome, onLogout }: InvestorDashboardProps) {
  const { user, signOut } = useAuth();
  // Balance Sheet Data - 2025 YTD Snapshot
  const balanceSheetData = {
    year: '2025',
    totalAssets: 302043.06,
    totalLiabilities: 240426.56,
    contributedCapitalEquity: 6054468.53,
    retainedEarnings: -6146475.79,
    netIncome: 191576.89,
    totalEquity: 61616.50,
    totalLiabilitiesEquity: 302043.06
  };

  // P&L Data for charts and table - Updated with COGS data
  const plData = [
    { 
      year: '2020', 
      totalIncome: 1146480,
      cogs: 283487,
      totalExpenses: 1420061, 
      netIncome: -557069
    },
    { 
      year: '2021', 
      totalIncome: 782731,
      cogs: 450873,
      totalExpenses: 881506, 
      netIncome: -549649
    },
    { 
      year: '2022', 
      totalIncome: 145207,
      cogs: 92576,
      totalExpenses: 788928, 
      netIncome: -736298
    },
    { 
      year: '2023', 
      totalIncome: 126834,
      cogs: 38050,
      totalExpenses: 409942, 
      netIncome: -317461
    },
    { 
      year: '2024', 
      totalIncome: 126728,
      cogs: 38018,
      totalExpenses: 192707, 
      netIncome: -102065
    },
    { 
      year: '2025', 
      totalIncome: 52608,
      cogs: 15782,
      totalExpenses: 109825, 
      netIncome: 191576
    }
  ];

  // P&L Totals for summary
  const plTotals = {
    totalIncome: 2380588,
    cogs: 918787,
    totalExpenses: 3802969,
    netIncome: -2070966
  };


  // Format currency values for display
  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return `${value.toLocaleString()}`;
  };

  // Format currency for table display
  const formatTableCurrency = (value: number) => {
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    let formatted = '';
    
    if (absValue >= 1000000) {
      formatted = `${(absValue / 1000000).toFixed(2)}M`;
    } else if (absValue >= 1000) {
      formatted = `${(absValue / 1000).toFixed(1)}K`;
    } else {
      formatted = `${absValue.toFixed(2)}`;
    }
    
    return isNegative ? `(${formatted})` : formatted;
  };


  // Safe display of user info
  const displayUserName = () => {
    if (user?.email) {
      // Extract name from email (everything before @)
      const emailName = user.email.split('@')[0];
      return emailName.toUpperCase();
    }
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name.toUpperCase();
    if (user?.user_metadata?.name) return user.user_metadata.name.toUpperCase();
    return 'INVESTOR';
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-cyan-500/20 sticky top-0 z-50">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4 max-w-full overflow-x-hidden">
          {/* Mobile: Two Row Layout */}
          <div className="md:hidden">
            {/* First Row: Logo and Buttons */}
            <div className="flex items-center justify-between mb-2">
              <div className="relative group flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <img 
                  src={logoImage} 
                  alt="Cosmic Forces Logo" 
                  className="h-6 w-auto transition-transform duration-500 group-hover:scale-105 relative z-10"
                />
              </div>
              
              <div className="flex items-center space-x-2 flex-shrink-0">
                {onHome && (
                  <button
                    onClick={onHome}
                    className="group flex items-center font-cyberpunk-nav text-cyan-400 hover:text-cyan-300 transition-all duration-300 cyberpunk-glow hover:scale-105 cursor-pointer touch-manipulation"
                  >
                    <div className="relative">
                      <Home className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
                      <div className="absolute inset-0 h-5 w-5 bg-cyan-400/20 rounded blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </button>
                )}
                {onLogout && (
                  <button
                    onClick={() => {
                      signOut();
                      onLogout();
                    }}
                    className="group flex items-center font-cyberpunk-nav text-red-400 hover:text-red-300 transition-all duration-300 cyberpunk-glow hover:scale-105 cursor-pointer touch-manipulation"
                  >
                    <div className="relative">
                      <LogOut className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
                      <div className="absolute inset-0 h-5 w-5 bg-red-400/20 rounded blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </button>
                )}
              </div>
            </div>
            
            {/* Second Row: Title and Status */}
            <div className="min-w-0">
              <h1 className="text-sm bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent font-cyberpunk truncate leading-tight mb-1">
                INVESTOR_PORTAL
              </h1>
              <p className="text-gray-400 font-cyberpunk-nav text-[10px] truncate leading-tight">
                STATUS: <span className="text-cyan-400">AUTHENTICATED</span> | USER: <span className="text-purple-400">{displayUserName()}</span>
              </p>
            </div>
          </div>

          {/* Desktop: Single Row Layout */}
          <div className="hidden md:flex items-center justify-between min-w-0 gap-2">
            <div className="flex items-center space-x-4 min-w-0 flex-1">
              {/* Logo */}
              <div className="relative group flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <img 
                  src={logoImage} 
                  alt="Cosmic Forces Logo" 
                  className="h-10 lg:h-12 w-auto transition-transform duration-500 group-hover:scale-105 relative z-10"
                />
              </div>
              
              {/* Title and Status */}
              <div className="min-w-0 flex-1">
                <h1 className="text-xl lg:text-2xl bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent font-cyberpunk truncate">
                  INVESTOR_PORTAL
                </h1>
                <p className="text-gray-400 font-cyberpunk-nav text-sm truncate">
                  STATUS: <span className="text-cyan-400">AUTHENTICATED</span> | USER: <span className="text-purple-400">{displayUserName()}</span>
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 flex-shrink-0">
              {onHome && (
                <button
                  onClick={onHome}
                  className="group flex items-center space-x-2 font-cyberpunk-nav text-cyan-400 hover:text-cyan-300 transition-all duration-300 cyberpunk-glow hover:scale-105 cursor-pointer touch-manipulation"
                >
                  <div className="relative">
                    <Home className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
                    <div className="absolute inset-0 h-5 w-5 bg-cyan-400/20 rounded blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <span className="relative">
                    <span className="relative z-10 text-sm">MAIN_SITE</span>
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-400/0 via-cyan-400/60 to-cyan-400/0 cyberpunk-underline"></div>
                  </span>
                  <div className="text-cyan-500/50 font-mono text-xs cyberpunk-bracket-pulse">[&gt;]</div>
                </button>
              )}
              {onLogout && (
                <button
                  onClick={() => {
                    signOut();
                    onLogout();
                  }}
                  className="group flex items-center space-x-2 font-cyberpunk-nav text-red-400 hover:text-red-300 transition-all duration-300 cyberpunk-glow hover:scale-105 cursor-pointer touch-manipulation"
                >
                  <div className="relative">
                    <LogOut className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
                    <div className="absolute inset-0 h-5 w-5 bg-red-400/20 rounded blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <span className="relative">
                    <span className="relative z-10 text-sm">SIGN_OUT</span>
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-red-400/0 via-red-400/60 to-red-400/0 cyberpunk-underline"></div>
                  </span>
                  <div className="text-red-500/50 font-mono text-xs cyberpunk-bracket-pulse">[X]</div>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 max-w-full overflow-x-hidden">
        {/* Game Gauntlet Section */}
        <div className="w-full mb-8 sm:mb-12">
          <div className="mb-4 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-cyberpunk bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent mb-2">
              🎮 GAME GAUNTLET
            </h2>
            <p className="text-gray-400 text-sm sm:text-base">Strategic gaming portfolio and investment opportunity</p>
          </div>
          
          <Card className="p-3 sm:p-6 lg:p-8 bg-slate-800/50 border border-cyan-500/20 hover:border-cyan-500/40 transition-all duration-500 group overflow-hidden">
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4 sm:gap-6 lg:gap-8">
              {/* Logo Section */}
              <div className="flex-shrink-0 w-full sm:w-auto flex justify-center lg:justify-start">
                <div className="relative group/logo max-w-full">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-xl blur-lg opacity-0 group-hover/logo:opacity-100 transition-opacity duration-500"></div>
                  <div className="h-24 sm:h-32 md:h-40 lg:h-48 w-24 sm:w-32 md:w-40 lg:w-48 flex items-center justify-center bg-slate-700/30 rounded-xl border border-cyan-500/20">
                    <img 
                      src="/images/logos/freepik__make-a-game-gauntlet-grasping-a-game-controller-in__34739@0.jpg" 
                      alt="Game Gauntlet Logo" 
                      className="h-full w-full object-contain transition-all duration-500 group-hover/logo:scale-105 relative z-10"
                      onError={(e) => {
                        console.error('Game Gauntlet logo failed to load:', e);
                        e.currentTarget.style.display = 'none';
                        // Show fallback text
                        const fallback = e.currentTarget.parentElement?.querySelector('.logo-fallback') as HTMLElement;
                        if (fallback) fallback.style.display = 'block';
                      }}
                      onLoad={() => {
                        console.log('Game Gauntlet logo loaded successfully');
                      }}
                    />
                    <div className="logo-fallback hidden text-cyan-400 font-cyberpunk text-xs sm:text-sm text-center">
                      GG
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="flex-1 text-center lg:text-left w-full min-w-0">
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-lg sm:text-xl lg:text-2xl text-white font-cyberpunk mb-3 break-words">Strategic Gaming Investment</h3>
                  <p className="text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed mb-4 break-words">
                    Game Gauntlet represents a significant opportunity in the interactive entertainment sector, 
                    combining innovative gameplay mechanics with proven market demand.
                  </p>
                  <div className="flex flex-wrap justify-center lg:justify-start gap-2 sm:gap-3 text-xs sm:text-sm text-gray-400">
                    <span className="bg-cyan-500/10 px-3 py-1 rounded border border-cyan-500/30">Gaming Portfolio</span>
                    <span className="bg-purple-500/10 px-3 py-1 rounded border border-purple-500/30">Market Opportunity</span>
                    <span className="bg-green-500/10 px-3 py-1 rounded border border-green-500/30">Strategic Asset</span>
                  </div>
                </div>

                {/* Pitch Deck Link */}
                <div className="bg-slate-700/30 rounded-lg p-3 sm:p-4 lg:p-6 border border-cyan-500/20 hover:border-cyan-500/40 transition-all duration-300 group/link overflow-hidden">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <h4 className="text-cyan-400 text-sm sm:text-base lg:text-lg font-cyberpunk break-words">INVESTMENT PITCH DECK</h4>
                    <div className="w-3 h-3 bg-cyan-400 rounded-full group-hover/link:animate-pulse flex-shrink-0"></div>
                  </div>
                  <p className="text-gray-300 text-xs sm:text-sm mb-3 sm:mb-4 break-words">
                    Access the comprehensive Game Gauntlet investment presentation and strategic analysis
                  </p>
                  <a 
                    href="/documents/gg-pitch-deck.pdf" 
                    className="inline-flex items-center space-x-2 sm:space-x-3 text-cyan-400 hover:text-cyan-300 font-cyberpunk-nav transition-all duration-300 group-hover/link:scale-105 break-words"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="text-sm sm:text-base lg:text-lg">VIEW PITCH DECK</span>
                    <div className="text-cyan-500/50 font-mono text-xs sm:text-sm cyberpunk-bracket-pulse flex-shrink-0">[&gt;]</div>
                  </a>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Annual Shareholder Meetings Section */}
        <div className="w-full mb-8 sm:mb-12">
          <div className="mb-4 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-cyberpunk bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent mb-2">
              📅 ANNUAL SHAREHOLDER MEETINGS
            </h2>
            <p className="text-gray-400 text-sm sm:text-base">Access meeting recordings and documentation</p>
          </div>
          
          <Card className="p-3 sm:p-6 bg-slate-800/50 border border-cyan-500/20 overflow-hidden">
            <div className="mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl text-white font-cyberpunk mb-2">9.11.2025</h3>
              <p className="text-gray-400 text-xs sm:text-sm">Annual Shareholder Meeting - Q3 2025</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Video Meeting Link */}
              <div className="bg-slate-700/30 rounded-lg p-4 sm:p-6 border border-cyan-500/20 hover:border-cyan-500/40 transition-all duration-300 group overflow-hidden">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h4 className="text-cyan-400 text-xs sm:text-sm font-cyberpunk break-words">VIDEO MEETING</h4>
                  <div className="w-3 h-3 bg-cyan-400 rounded-full group-hover:animate-pulse flex-shrink-0"></div>
                </div>
                <p className="text-gray-300 text-xs sm:text-sm mb-3 sm:mb-4 break-words">Access the recorded shareholder meeting video</p>
                <a 
                  href="https://youtu.be/XOUcySSaTOw" 
                  className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 font-cyberpunk-nav transition-colors duration-300 group-hover:scale-105"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open("https://youtu.be/XOUcySSaTOw", "_blank", "noopener,noreferrer");
                  }}
                >
                  <span>WATCH RECORDING</span>
                  <div className="text-cyan-500/50 font-mono text-xs cyberpunk-bracket-pulse">[&gt;]</div>
                </a>
              </div>

              {/* Meeting Minutes Link */}
              <div className="bg-slate-700/30 rounded-lg p-4 sm:p-6 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 group overflow-hidden">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h4 className="text-purple-400 text-xs sm:text-sm font-cyberpunk break-words">MEETING MINUTES</h4>
                  <div className="w-3 h-3 bg-purple-400 rounded-full group-hover:animate-pulse flex-shrink-0"></div>
                </div>
                <p className="text-gray-300 text-xs sm:text-sm mb-3 sm:mb-4 break-words">View the official meeting minutes document</p>
                <a 
                  href="https://docs.google.com/document/d/1zkZbRmR4l0rLOWwfq7iV528Gcdwk7j9MXgHS4WduPgw/edit?usp=sharing" 
                  className="inline-flex items-center space-x-2 text-purple-400 hover:text-purple-300 font-cyberpunk-nav transition-colors duration-300 group-hover:scale-105"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open("https://docs.google.com/document/d/1zkZbRmR4l0rLOWwfq7iV528Gcdwk7j9MXgHS4WduPgw/edit?usp=sharing", "_blank", "noopener,noreferrer");
                  }}
                >
                  <span>VIEW MINUTES</span>
                  <div className="text-purple-500/50 font-mono text-xs cyberpunk-bracket-pulse">[&gt;]</div>
                </a>
              </div>
            </div>
          </Card>
        </div>

        {/* Financial Reports Section */}
        <div className="w-full">
          <div className="mb-8">
            <h2 className="text-3xl font-cyberpunk bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent mb-2">
              📋 FINANCIAL REPORTS
            </h2>
            <p className="text-gray-400 text-base">Comprehensive financial data and performance metrics</p>
          </div>
          <div className="space-y-8 financial-data">
            {/* Balance Sheet Financial Section */}
            <Card className="p-6 bg-slate-800/50 border border-purple-500/20 financial-data selectable-text">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl text-white font-cyberpunk mb-2">Balance Sheet Financials</h3>
                  <p className="text-gray-400 text-sm">2025 YTD Financial Position Snapshot</p>
                </div>
                <BarChart3 className="h-6 w-6 text-purple-400" />
              </div>

              {/* Balance Sheet Snapshot - Vertical Layout with Cyberpunk Frame */}
              <div className="relative max-w-3xl mx-auto">
                {/* Cyberpunk Corner Decorations */}
                <div className="absolute -top-2 -left-2 w-6 h-6 border-l-2 border-t-2 border-purple-500/40"></div>
                <div className="absolute -top-2 -right-2 w-6 h-6 border-r-2 border-t-2 border-purple-500/40"></div>
                <div className="absolute -bottom-2 -left-2 w-6 h-6 border-l-2 border-b-2 border-purple-500/40"></div>
                <div className="absolute -bottom-2 -right-2 w-6 h-6 border-r-2 border-b-2 border-purple-500/40"></div>
                
                {/* Scanning Line Animation */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent animate-pulse"></div>
                
                {/* Side Accent Lines */}
                <div className="absolute left-0 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-purple-500/30 to-transparent"></div>
                <div className="absolute right-0 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-purple-500/30 to-transparent"></div>
                
                {/* Data Timestamp Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 px-2 sm:px-3 py-2 bg-slate-900/30 border border-purple-500/10 rounded">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse"></div>
                    <span className="text-xs font-cyberpunk-nav text-cyan-400/80">DATA_SNAPSHOT</span>
                  </div>
                  <div className="flex items-center space-x-2 sm:space-x-3 text-xs font-mono text-gray-500">
                    <span>STATUS: <span className="text-green-400">ACTIVE</span></span>
                    <span className="text-purple-400">|</span>
                    <span>YTD_2025</span>
                  </div>
                </div>

              <div className="space-y-3 mb-6 relative z-10">
                {/* Total Assets */}
                <div className="relative bg-gradient-to-r from-slate-700/40 to-slate-800/40 rounded-lg p-3 sm:p-4 border border-cyan-500/30 hover:border-cyan-500/60 transition-all duration-300 group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full group-hover:animate-pulse flex-shrink-0"></div>
                      <h4 className="text-cyan-400 text-xs sm:text-sm md:text-base font-cyberpunk tracking-wider truncate">TOTAL ASSETS</h4>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg sm:text-xl md:text-2xl font-cyberpunk text-white cyberpunk-glow whitespace-nowrap">${formatCurrency(balanceSheetData.totalAssets)}</div>
                      <div className="text-xs text-gray-400 font-cyberpunk-nav">2025 YTD</div>
                    </div>
                  </div>
                </div>

                {/* Total Liabilities */}
                <div className="relative bg-gradient-to-r from-slate-700/40 to-slate-800/40 rounded-lg p-3 sm:p-4 border border-pink-500/30 hover:border-pink-500/60 transition-all duration-300 group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className="w-2 h-2 bg-pink-400 rounded-full group-hover:animate-pulse flex-shrink-0"></div>
                      <h4 className="text-pink-400 text-xs sm:text-sm md:text-base font-cyberpunk tracking-wider truncate">TOTAL LIABILITIES</h4>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg sm:text-xl md:text-2xl font-cyberpunk text-white cyberpunk-glow whitespace-nowrap">${formatCurrency(balanceSheetData.totalLiabilities)}</div>
                      <div className="text-xs text-gray-400 font-cyberpunk-nav">2025 YTD</div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-purple-500/20 my-4"></div>

                {/* Contributed Capital - Equity */}
                <div className="relative bg-gradient-to-r from-slate-700/40 to-slate-800/40 rounded-lg p-3 sm:p-4 border border-green-500/30 hover:border-green-500/60 transition-all duration-300 group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full group-hover:animate-pulse flex-shrink-0"></div>
                      <h4 className="text-green-400 text-xs sm:text-sm md:text-base font-cyberpunk tracking-wider truncate">CONTRIBUTED CAPITAL</h4>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg sm:text-xl md:text-2xl font-cyberpunk text-white cyberpunk-glow whitespace-nowrap">${formatCurrency(balanceSheetData.contributedCapitalEquity)}</div>
                      <div className="text-xs text-gray-400 font-cyberpunk-nav">EQUITY • 2025 YTD</div>
                    </div>
                  </div>
                </div>

                {/* Retained Earnings */}
                <div className="relative bg-gradient-to-r from-slate-700/40 to-slate-800/40 rounded-lg p-3 sm:p-4 border border-orange-500/30 hover:border-orange-500/60 transition-all duration-300 group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className="w-2 h-2 bg-orange-400 rounded-full group-hover:animate-pulse flex-shrink-0"></div>
                      <h4 className="text-orange-400 text-xs sm:text-sm md:text-base font-cyberpunk tracking-wider truncate">RETAINED EARNINGS</h4>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg sm:text-xl md:text-2xl font-cyberpunk text-white cyberpunk-glow whitespace-nowrap">(${formatCurrency(Math.abs(balanceSheetData.retainedEarnings))})</div>
                      <div className="text-xs text-gray-400 font-cyberpunk-nav">2025 YTD</div>
                    </div>
                  </div>
                </div>

                {/* Net Income */}
                <div className="relative bg-gradient-to-r from-slate-700/40 to-slate-800/40 rounded-lg p-3 sm:p-4 border border-blue-500/30 hover:border-blue-500/60 transition-all duration-300 group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className="w-2 h-2 bg-blue-400 rounded-full group-hover:animate-pulse flex-shrink-0"></div>
                      <h4 className="text-blue-400 text-xs sm:text-sm md:text-base font-cyberpunk tracking-wider truncate">NET INCOME</h4>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg sm:text-xl md:text-2xl font-cyberpunk text-white cyberpunk-glow whitespace-nowrap">${formatCurrency(balanceSheetData.netIncome)}</div>
                      <div className="text-xs text-gray-400 font-cyberpunk-nav">2025 YTD</div>
                    </div>
                  </div>
                </div>

                {/* Total Equity */}
                <div className="relative bg-gradient-to-r from-slate-700/40 to-slate-800/40 rounded-lg p-3 sm:p-4 border border-purple-500/30 hover:border-purple-500/60 transition-all duration-300 group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className="w-2 h-2 bg-purple-400 rounded-full group-hover:animate-pulse flex-shrink-0"></div>
                      <h4 className="text-purple-400 text-xs sm:text-sm md:text-base font-cyberpunk tracking-wider truncate">TOTAL EQUITY</h4>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg sm:text-xl md:text-2xl font-cyberpunk text-white cyberpunk-glow whitespace-nowrap">${formatCurrency(balanceSheetData.totalEquity)}</div>
                      <div className="text-xs text-gray-400 font-cyberpunk-nav">2025 YTD</div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-purple-500/20 my-4"></div>

                {/* Total Liabilities and Equity */}
                <div className="relative bg-gradient-to-r from-slate-700/40 to-slate-800/40 rounded-lg p-3 sm:p-4 border border-yellow-500/30 hover:border-yellow-500/60 transition-all duration-300 group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full group-hover:animate-pulse flex-shrink-0"></div>
                      <h4 className="text-yellow-400 text-xs sm:text-sm md:text-base font-cyberpunk tracking-wider truncate">TOTAL LIABILITIES & EQUITY</h4>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg sm:text-xl md:text-2xl font-cyberpunk text-white cyberpunk-glow whitespace-nowrap">${formatCurrency(balanceSheetData.totalLiabilitiesEquity)}</div>
                      <div className="text-xs text-gray-400 font-cyberpunk-nav">2025 YTD</div>
                    </div>
                  </div>
                </div>
              </div>

                {/* Bottom Snapshot Info */}
                <div className="mt-4 px-2 py-2 bg-slate-900/30 border border-purple-500/10 rounded">
                  <div className="flex items-center justify-between text-xs font-mono text-gray-500">
                    <div className="flex items-center space-x-2">
                      <div className="w-1 h-1 bg-purple-400 rounded-full"></div>
                      <span>SCAN_COMPLETE</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-purple-400">█▓▒░</span>
                      <span>7_RECORDS</span>
                    </div>
                  </div>
                </div>
              </div>

            </Card>

            {/* P&L Statement Section */}
            <Card className="p-6 bg-slate-800/50 border border-cyan-500/20 financial-data selectable-text">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl text-white font-cyberpunk mb-2">P&L Statement</h3>
                  <p className="text-gray-400 text-sm">Profit and loss performance analysis (2020-2025) <span className="text-cyan-400/70">• 2025 YTD</span></p>
                </div>
                <DollarSign className="h-6 w-6 text-cyan-400" />
              </div>

              {/* P&L Combined Chart and Highlights - Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Chart Container - Takes 2/3 of the space */}
                <div className="lg:col-span-2 bg-slate-700/30 rounded-lg p-6 border border-cyan-500/20">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                    <h4 className="text-cyan-400 text-lg font-cyberpunk">P&L PERFORMANCE OVERVIEW</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-sm">
                      <div className="text-center sm:text-right">
                        <div className="text-white font-cyberpunk text-xs sm:text-sm">{formatCurrency(plData[plData.length - 1].totalIncome)}</div>
                        <div className="text-cyan-400 text-xs">2025 Income</div>
                      </div>
                      <div className="text-center sm:text-right">
                        <div className="text-white font-cyberpunk text-xs sm:text-sm">{formatCurrency(plData[plData.length - 1].cogs)}</div>
                        <div className="text-pink-400 text-xs">2025 COGS</div>
                      </div>
                      <div className="text-center sm:text-right">
                        <div className="text-white font-cyberpunk text-xs sm:text-sm">{formatCurrency(plData[plData.length - 1].totalExpenses)}</div>
                        <div className="text-orange-400 text-xs">2025 Expenses</div>
                      </div>
                      <div className="text-center sm:text-right">
                        <div className="text-white font-cyberpunk text-xs sm:text-sm">{formatCurrency(plData[plData.length - 1].netIncome)}</div>
                        <div className="text-purple-400 text-xs">2025 Net Income</div>
                      </div>
                    </div>
                  </div>
                  <div className="h-64 sm:h-72 lg:h-80">
                    <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={plData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(34, 211, 238, 0.1)" />
                      <XAxis 
                        dataKey="year" 
                        stroke="#64748b" 
                        tick={{ fill: '#64748b', fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#64748b" 
                        tick={{ fill: '#64748b', fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatCurrency}
                        domain={[-800000, 1500000]}
                        ticks={[-800000, -400000, 0, 400000, 800000, 1200000, 1500000]}
                      />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(15, 23, 42, 0.95)', 
                            border: '1px solid rgba(34, 211, 238, 0.3)',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <ReferenceLine y={0} stroke="rgba(148, 163, 184, 0.5)" strokeDasharray="2 2" />
                      <Line 
                        type="monotone" 
                        dataKey="totalIncome" 
                        stroke="#22d3ee" 
                        strokeWidth={2}
                        dot={{ fill: '#22d3ee', r: 3 }}
                        activeDot={{ r: 5, fill: '#22d3ee' }}
                        name="Total Income"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="cogs" 
                        stroke="#ec4899" 
                        strokeWidth={2}
                        dot={{ fill: '#ec4899', r: 3 }}
                        activeDot={{ r: 5, fill: '#ec4899' }}
                        name="COGS"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="totalExpenses" 
                        stroke="#fb923c" 
                        strokeWidth={2}
                        dot={{ fill: '#fb923c', r: 3 }}
                        activeDot={{ r: 5, fill: '#fb923c' }}
                        name="Total Expenses"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="netIncome" 
                        stroke="#a855f7" 
                        strokeWidth={2}
                        dot={{ fill: '#a855f7', r: 3 }}
                        activeDot={{ r: 5, fill: '#a855f7' }}
                        name="Net Income"
                      />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Highlights Container - Takes 1/3 of the space */}
                <div className="lg:col-span-1 bg-slate-700/30 rounded-lg p-4 sm:p-6 border border-cyan-500/20">
                  <h4 className="text-cyan-400 text-base sm:text-lg font-cyberpunk mb-4 sm:mb-6">KEY HIGHLIGHTS</h4>
                  
                  {/* Income Highlights */}
                  <div className="mb-4 sm:mb-6">
                    <div className="flex items-center mb-2 sm:mb-3">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-cyan-400 rounded-full mr-2 sm:mr-3"></div>
                      <span className="text-cyan-400 font-cyberpunk text-xs sm:text-sm">TOTAL INCOME</span>
                    </div>
                    <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Peak Income:</span>
                        <span className="text-white font-cyberpunk text-right">{formatCurrency(Math.max(...plData.map(d => d.totalIncome)))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">6-Year Total:</span>
                        <span className="text-white font-cyberpunk text-right">{formatCurrency(plTotals.totalIncome)}</span>
                      </div>
                    </div>
                  </div>

                  {/* COGS Highlights */}
                  <div className="mb-4 sm:mb-6">
                    <div className="flex items-center mb-2 sm:mb-3">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-pink-400 rounded-full mr-2 sm:mr-3"></div>
                      <span className="text-pink-400 font-cyberpunk text-xs sm:text-sm">COGS</span>
                    </div>
                    <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Peak COGS:</span>
                        <span className="text-white font-cyberpunk text-right">{formatCurrency(Math.max(...plData.map(d => d.cogs)))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">6-Year Total:</span>
                        <span className="text-white font-cyberpunk text-right">{formatCurrency(plTotals.cogs)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Expenses Highlights */}
                  <div className="mb-4 sm:mb-6">
                    <div className="flex items-center mb-2 sm:mb-3">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-orange-400 rounded-full mr-2 sm:mr-3"></div>
                      <span className="text-orange-400 font-cyberpunk text-xs sm:text-sm">TOTAL EXPENSES</span>
                    </div>
                    <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Peak Expenses:</span>
                        <span className="text-white font-cyberpunk text-right">{formatCurrency(Math.max(...plData.map(d => d.totalExpenses)))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">6-Year Total:</span>
                        <span className="text-white font-cyberpunk text-right">{formatCurrency(plTotals.totalExpenses)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Net Income Highlights */}
                  <div>
                    <div className="flex items-center mb-2 sm:mb-3">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-purple-400 rounded-full mr-2 sm:mr-3"></div>
                      <span className="text-purple-400 font-cyberpunk text-xs sm:text-sm">NET INCOME</span>
                    </div>
                    <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Best Net:</span>
                        <span className="text-white font-cyberpunk text-right">{formatCurrency(Math.max(...plData.map(d => d.netIncome)))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">6-Year Net:</span>
                        <span className="text-white font-cyberpunk text-right">{formatCurrency(plTotals.netIncome)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* P&L Data Table */}
              <div className="bg-slate-900/50 rounded-lg p-6 border border-cyan-500/10">
                <h4 className="text-cyan-400 font-cyberpunk text-base mb-4">P&L Statement Data (2020-2025)</h4>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-cyan-500/20">
                        <th className="text-left text-cyan-400 font-cyberpunk py-2 px-1 sm:px-2">YEAR</th>
                        <th className="text-right text-cyan-400 font-cyberpunk py-2 px-1 sm:px-2">TOTAL INCOME</th>
                        <th className="text-right text-pink-400 font-cyberpunk py-2 px-1 sm:px-2">COGS</th>
                        <th className="text-right text-orange-400 font-cyberpunk py-2 px-1 sm:px-2">TOTAL EXPENSES</th>
                        <th className="text-right text-purple-400 font-cyberpunk py-2 px-1 sm:px-2">NET INCOME</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plData.map((item, index) => (
                        <tr key={index} className={`border-b border-gray-700/30 ${item.year === '2025' ? 'bg-cyan-500/5' : ''}`}>
                          <td className="text-white font-cyberpunk py-2 px-1 sm:px-2">
                            {item.year}
                            {item.year === '2025' && <span className="text-cyan-400/60 text-xs ml-1 sm:ml-2">YTD</span>}
                          </td>
                          <td className="text-right text-gray-300 py-2 px-1 sm:px-2">{formatTableCurrency(item.totalIncome)}</td>
                          <td className="text-right text-gray-300 py-2 px-1 sm:px-2">{formatTableCurrency(item.cogs)}</td>
                          <td className="text-right text-gray-300 py-2 px-1 sm:px-2">{formatTableCurrency(item.totalExpenses)}</td>
                          <td className="text-right text-gray-300 py-2 px-1 sm:px-2">{formatTableCurrency(item.netIncome)}</td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-cyan-500/30 text-cyan-300">
                        <td className="text-cyan-400 font-cyberpunk py-3 px-1 sm:px-2">TOTALS</td>
                        <td className="text-right py-3 px-1 sm:px-2">{formatTableCurrency(plTotals.totalIncome)}</td>
                        <td className="text-right py-3 px-1 sm:px-2">{formatTableCurrency(plTotals.cogs)}</td>
                        <td className="text-right py-3 px-1 sm:px-2">{formatTableCurrency(plTotals.totalExpenses)}</td>
                        <td className="text-right py-3 px-1 sm:px-2">{formatTableCurrency(plTotals.netIncome)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>


          </div>
        </div>
      </div>
    </div>
  );
}